import type { Facing, Scene } from '../../../shared/protocol';
import type { GbaRam } from '../gba-ram';
import { decodeGameText } from './charset';
import { readPartyLead } from './party';
import { activeSymbols, type FireRedSymbols } from './symbols';

export type { Facing, Scene };

const OBJECT_EVENT_SIZE = 0x24;
const OBJECT_EVENTS_COUNT = 16;
export const MAP_OFFSET = 7; // coordenadas dos objetos têm +7 em relação ao mapa

// struct ObjectEvent (include/global.fieldmap.h)
const OE_FLAGS = 0x00; // bit 0 = active, bit 16 = isPlayer
const OE_CURRENT_COORDS = 0x10;
const OE_DIRECTIONS = 0x18; // nibble baixo = facingDirection, alto = movementDirection

// struct PlayerAvatar
const PA_FLAGS = 0x00;
const PA_RUNNING_STATE = 0x02;
const PA_OBJECT_EVENT_ID = 0x05;
const PA_GENDER = 0x07;
const AVATAR_FAST = 0x02 | 0x04 | 0x80; // MACH_BIKE | ACRO_BIKE | DASH (correndo)
const AVATAR_SURFING = 0x08;
const SB2_NAME = 0x00; // SaveBlock2.playerName (7 letras + terminador)

// struct SaveBlock1: pos (Coords16) em 0x00 = posição da câmera; location (WarpData) em 0x04
const SB1_POS = 0x00;
const SB1_LOCATION = 0x04;

// struct CameraObject (include/field_camera.h)
const CAMERA_X = 0x10;
const CAMERA_Y = 0x14;

const FACING: Record<number, Facing> = { 1: 'down', 2: 'up', 3: 'left', 4: 'right' };

export interface PlayerState {
  mapGroup: number;
  mapNum: number;
  /** Coordenadas em tiles dentro do mapa atual. */
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
  /** Correndo ou de bicicleta. */
  fast: boolean;
  gender: 0 | 1;
  scene: Scene;
  /** Nome do treinador no jogo (o "nick" escolhido no cadastro). */
  nick: string;
  /** Espécie do 1º da equipe, que segue o jogador (0 = ninguém: equipe vazia, ovo ou surfando). */
  lead: number;
}

const isEwramPointer = (ptr: number): boolean => ptr >>> 24 === 0x02;

export function readScene(ram: GbaRam, sym: FireRedSymbols = activeSymbols()): Scene {
  const callback2 = ram.u32(sym.gMain + 4) & ~1;
  if (callback2 === sym.CB2_Overworld) return 'overworld';
  if (callback2 === sym.BattleMainCB2) return 'battle';
  return 'other';
}

/** Lê o estado do jogador. Retorna null fora do jogo (tela título, intro etc.). */
export function readPlayer(ram: GbaRam, sym: FireRedSymbols = activeSymbols()): PlayerState | null {
  const saveBlock1 = ram.u32(sym.gSaveBlock1Ptr);
  if (!isEwramPointer(saveBlock1)) return null;

  const objectId = ram.u8at(sym.gPlayerAvatar + PA_OBJECT_EVENT_ID);
  if (objectId >= OBJECT_EVENTS_COUNT) return null;

  const object = sym.gObjectEvents + objectId * OBJECT_EVENT_SIZE;
  const flags = ram.u32(object + OE_FLAGS);
  const active = (flags & 1) !== 0;
  const isPlayer = ((flags >>> 16) & 1) !== 0;
  if (!active || !isPlayer) return null;

  const facing = FACING[ram.u8at(object + OE_DIRECTIONS) & 0x0f];
  if (!facing) return null;

  const avatarFlags = ram.u8at(sym.gPlayerAvatar + PA_FLAGS);
  const saveBlock2 = ram.u32(sym.gSaveBlock2Ptr);
  const lead = readPartyLead(ram, sym);

  return {
    mapGroup: ram.s8(saveBlock1 + SB1_LOCATION),
    mapNum: ram.s8(saveBlock1 + SB1_LOCATION + 1),
    x: ram.s16(object + OE_CURRENT_COORDS) - MAP_OFFSET,
    y: ram.s16(object + OE_CURRENT_COORDS + 2) - MAP_OFFSET,
    facing,
    moving: ram.u8at(sym.gPlayerAvatar + PA_RUNNING_STATE) === 2,
    fast: (avatarFlags & AVATAR_FAST) !== 0,
    gender: ram.u8at(sym.gPlayerAvatar + PA_GENDER) === 1 ? 1 : 0,
    scene: readScene(ram, sym),
    nick: isEwramPointer(saveBlock2) ? decodeGameText(ram.bytes(saveBlock2 + SB2_NAME, 8)) : '',
    lead: lead && !lead.isEgg && !(avatarFlags & AVATAR_SURFING) ? lead.species : 0,
  };
}

/** O que o jogo usa para posicionar sprites de objetos na tela. */
export interface CameraState {
  /** gSaveBlock1Ptr->pos: tile (sem MAP_OFFSET) para onde a câmera aponta. */
  posX: number;
  posY: number;
  /** Deslocamento em pixels (-15..15) enquanto a câmera desliza entre tiles. */
  subX: number;
  subY: number;
  panX: number;
  panY: number;
}

export function readCamera(ram: GbaRam, sym: FireRedSymbols = activeSymbols()): CameraState | null {
  const saveBlock1 = ram.u32(sym.gSaveBlock1Ptr);
  if (!isEwramPointer(saveBlock1)) return null;
  return {
    posX: ram.s16(saveBlock1 + SB1_POS),
    posY: ram.s16(saveBlock1 + SB1_POS + 2),
    subX: ram.u32(sym.gFieldCamera + CAMERA_X) | 0,
    subY: ram.u32(sym.gFieldCamera + CAMERA_Y) | 0,
    panX: ram.s16(sym.sHorizontalCameraPan),
    panY: ram.s16(sym.sVerticalCameraPan),
  };
}

/**
 * Canto superior esquerdo (em pixels da tela do GBA, 240x160) de um sprite 16x32
 * em pé no tile (x, y) do mapa — aceita frações para animar entre tiles.
 * Mesma conta de SetSpritePosToMapCoords + gSpriteCoordOffset (field_camera.c,
 * event_object_movement.c), com o ajuste de +16 sempre que a câmera está no meio
 * de um passo.
 */
export function tileToScreen(camera: CameraState, x: number, y: number): { left: number; top: number } {
  const adjust = (sub: number) => (sub > 0 ? 16 : sub < 0 ? -16 : 0);
  const left = (x + MAP_OFFSET - camera.posX) * 16 - camera.subX + adjust(camera.subX) - camera.panX;
  const top = (y + MAP_OFFSET - camera.posY) * 16 - camera.subY + adjust(camera.subY) - camera.panY - 8 - 16;
  return { left, top };
}
